import React, { useState, useEffect, useRef } from 'react';
import '../styles/AboutUs.css';

const AboutUs: React.FC = () => {
  const [visibleSections, setVisibleSections] = useState<number[]>([]);
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = sectionRefs.current.findIndex(ref => ref === entry.target);
            if (index !== -1 && !visibleSections.includes(index)) {
              setVisibleSections(prev => [...prev, index]);
            }
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    sectionRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, [visibleSections]);

  return (
    <section className="about-us">
      <div className="container">
        <h2 className="section-title">About Us</h2>
        
        <div className="about-content">
          <div 
            ref={(el) => { sectionRefs.current[0] = el; }}
            className={`about-section ${visibleSections.includes(0) ? 'visible' : ''}`}
          >
            <h3 className="subsection-title">Who We Are</h3>
            <p className="subsection-text">
              We are a <strong>Swiss-based technology company</strong> committed to shaping the future of enterprise automation through artificial intelligence. Our team brings together expertise in engineering, business, and research, with strong academic foundations and a shared drive to make AI truly useful in real-world settings.
            </p>
          </div>

          <div 
            ref={(el) => { sectionRefs.current[1] = el; }}
            className={`about-section ${visibleSections.includes(1) ? 'visible' : ''}`}
          >
            <h3 className="subsection-title">What We Do</h3>
            <p className="subsection-text">
              We build <strong>intelligent AI agents</strong> powered by large language models (LLMs) to help enterprises <strong>streamline workflows, reduce manual effort, and enhance decision-making</strong>. Our solutions are secure, adaptable, and aligned with the complex needs of enterprise environments.
            </p>
          </div>

          <div 
            ref={(el) => { sectionRefs.current[2] = el; }}
            className={`about-section ${visibleSections.includes(2) ? 'visible' : ''}`}
          >
            <h3 className="subsection-title">Our Mission</h3>
            <p className="subsection-text">
              To translate <strong>cutting-edge AI research</strong> into meaningful, efficient, and scalable enterprise solutions — empowering organizations to <strong>work smarter, move faster, and lead confidently</strong> in the digital era.
            </p>
          </div>

          <div 
            ref={(el) => { sectionRefs.current[3] = el; }}
            className={`about-section ${visibleSections.includes(3) ? 'visible' : ''}`}
          >
            <h3 className="subsection-title">Why Switzerland</h3>
            <p className="subsection-text">
              Based in Switzerland, we embrace the values of <strong>precision, integrity, and innovation</strong>. Our location enables close collaboration with leading academic institutions and offers a <strong>stable, trusted environment</strong> for building world-class technology.
            </p>
          </div>

          <div 
            ref={(el) => { sectionRefs.current[4] = el; }}
            className={`about-section ${visibleSections.includes(4) ? 'visible' : ''}`}
          >
            <h3 className="subsection-title">Our Vision</h3>
            <p className="subsection-text">
              A future where AI agents are not just tools, but <strong>trusted digital teammates</strong> — seamlessly integrated into the day-to-day of enterprise life, across industries and borders.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutUs;
